import type { ApprovalDecision, ApprovalRequestRecord } from '@nadoutong/contracts';
import { parseCreditApplicationPayload, parseTemporaryCreditApplicationPayload } from '@nadoutong/contracts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useApprovalQueue, useDecideApproval } from '../../api/approvals.ts';
import { OpsApiError } from '../../api/client.ts';
import { CustomerRef } from '../../components/identity/CustomerRef.tsx';
import { OpsUserRef } from '../../components/identity/OpsUserRef.tsx';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import { formatAgeShort } from '../../lib/approvals.ts';
import {
  Button,
  Chip,
  Dialog,
  EmptyState,
  FormField,
  Pagination,
  Table,
  TableEmpty,
  TableSkeleton,
  TBody,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { CreditApplicationDetail } from './CreditApplicationDetail.tsx';
import { TemporaryCreditDetail } from './TemporaryCreditDetail.tsx';
import { approvalOrgTargetId, KIND_LABEL_KEYS, summarizeApprovalPayloadForKind } from './kindPresentation.ts';

/**
 * Approver queue tab (extracted from ApprovalCenterPage.tsx in E3-G2
 * #1103, #1098 deferred item 18 — behavior-preserving move).
 *
 * Decide race (E1-S3 AC 3): a 409 `APPROVAL_REQUEST_NOT_PENDING` is
 * NOT an error toast — the row flips to an inline "already decided"
 * state and the queue + shell chip refetch. Own requests render the
 * same disabled treatment (the server bans self-decides).
 */

const PAGE_SIZE = 50;

interface DecideTarget {
  readonly request: ApprovalRequestRecord;
  readonly decision: ApprovalDecision;
}

/** The expanded credit-detail row's DOM id, referenced by the toggle's
 * `aria-controls` (#1957). One place so the two cannot disagree. */
function creditDetailRowId(requestId: string): string {
  return `approval-credit-detail-${requestId}`;
}

export function QueueTab(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const sessionOpsUserId = user?.opsUserId ?? null;
  const [offset, setOffset] = useState(0);
  const query = useApprovalQueue({ limit: PAGE_SIZE, offset }, true);
  const decide = useDecideApproval();

  const [target, setTarget] = useState<DecideTarget | null>(null);
  const [note, setNote] = useState('');
  // Rows whose decide race we lost: kept locally so the "already
  // decided" state survives the refetch that removes them from the
  // pending queue (AC 3).
  const [decidedElsewhere, setDecidedElsewhere] = useState<
    ReadonlyMap<string, ApprovalRequestRecord>
  >(new Map());

  // #1957: rows expanded into their credit-application detail.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  function toggle(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const nowMs = Date.now();
  const data = query.data;

  const rows = useMemo<readonly ApprovalRequestRecord[]>(() => {
    const live = data?.requests ?? [];
    const liveIds = new Set(live.map((row) => row.id));
    const stale = [...decidedElsewhere.values()].filter((row) => !liveIds.has(row.id));
    return [...stale, ...live];
  }, [data, decidedElsewhere]);

  function openDecide(request: ApprovalRequestRecord, decision: ApprovalDecision): void {
    setNote('');
    setTarget({ request, decision });
  }

  function submitDecision(): void {
    if (target === null || decide.isPending) {
      return;
    }
    const { request, decision } = target;
    const trimmed = note.trim();
    if (decision === 'rejected' && trimmed.length === 0) {
      return;
    }
    decide.mutate(
      { id: request.id, decision, note: trimmed.length > 0 ? trimmed : null },
      {
        onSuccess: () => {
          setTarget(null);
          toast.success(
            decision === 'approved' ? t('approvals.toast.approved') : t('approvals.toast.rejected'),
          );
        },
        onError: (err) => {
          if (err instanceof OpsApiError && err.code === 'APPROVAL_REQUEST_NOT_PENDING') {
            // Lost the decide race: flip the row to its inline
            // "already decided" state — deliberately no error toast.
            setDecidedElsewhere((prev) => new Map(prev).set(request.id, request));
            setTarget(null);
            return;
          }
          if (err instanceof OpsApiError && err.code === 'APPROVAL_STAGE_ACTOR_CONFLICT') {
            // Four-eyes: the approver already decided a prior stage of this
            // request (epic #1033 E4-C2, #1126). A distinct approver must
            // decide this stage — surface a specific message, not the raw
            // server string.
            setTarget(null);
            toast.error(t('approvals.toast.stage_actor_conflict'));
            return;
          }
          toast.error(coerceErrorMessage(err));
        },
      },
    );
  }

  if (query.isPending) {
    return <TableSkeleton rows={5} testId="approvals-queue-loading" />;
  }
  if (query.isError || data === undefined) {
    return (
      <EmptyState
        title={t('approvals.load_error')}
        hint={coerceErrorMessage(query.error)}
        action={
          <Button
            onClick={() => {
              void query.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        }
        testId="approvals-queue-error"
      />
    );
  }

  const pageCount = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const rejectNoteMissing = target?.decision === 'rejected' && note.trim().length === 0;

  return (
    <div className="flex flex-col gap-3">
      <Table testId="approvals-queue-table">
        <THead>
          <Tr interactive={false}>
            <Th>{t('approvals.col.kind')}</Th>
            <Th>{t('approvals.col.customer')}</Th>
            <Th>{t('approvals.col.target')}</Th>
            <Th>{t('approvals.col.requester')}</Th>
            <Th num>{t('approvals.col.age')}</Th>
            <Th>{t('approvals.col.summary')}</Th>
            <Th>{t('approvals.col.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.length === 0 ? (
            <TableEmpty colSpan={7} testId="approvals-queue-empty">
              {t('approvals.queue.empty')}
            </TableEmpty>
          ) : (
            rows.map((request) => {
              const summary = summarizeApprovalPayloadForKind(request.kind, request.payload);
              const lostRace = decidedElsewhere.has(request.id);
              // UI truth for the server's self-approval 403 (#1098): an
              // approver can never decide their own request, so the row
              // renders the same disabled treatment as a lost race
              // instead of buttons that only fail on click.
              const ownRequest =
                sessionOpsUserId !== null && request.requester_ops_user_id === sessionOpsUserId;
              // #1957: the credit_application row can expand into the contract
              // that justifies the limit. `parse…` returning null (an
              // out-of-band payload) leaves the row exactly as it was — no
              // toggle rather than a toggle that opens onto nothing.
              const creditDetail =
                request.kind === 'credit_application'
                  ? parseCreditApplicationPayload(request.payload)
                  : null;
              const temporaryDetail =
                request.kind === 'temporary_credit_application'
                  ? parseTemporaryCreditApplicationPayload(request.payload)
                  : null;
              const expandableDetail = creditDetail ?? temporaryDetail;
              const isExpanded = expanded.has(request.id);
              return [
                <Tr key={request.id} data-testid={`approval-queue-row-${request.id}`}>
                  <Td emphasis>
                    <div className="flex flex-col gap-1">
                      <span>{t(KIND_LABEL_KEYS[request.kind])}</span>
                      {request.required_stages > 1 ? (
                        <Chip tone="signal" testId={`approval-stage-chip-${request.id}`}>
                          {t('approvals.stage.chip', {
                            current: String(request.current_stage),
                            total: String(request.required_stages),
                          })}
                        </Chip>
                      ) : null}
                    </div>
                  </Td>
                  <Td>
                    <span data-testid={`approval-queue-customer-${request.id}`}>
                      {request.credit_customer?.org_name ?? t('credit.overview.dash')}
                    </span>
                  </Td>
                  <Td mono>
                    <ApprovalTargetRef request={request} />
                  </Td>
                  <Td mono>
                    <OpsUserRef id={request.requester_ops_user_id} />
                  </Td>
                  <Td num>{formatAgeShort(request.created_at, nowMs)}</Td>
                  <Td>
                    <div className="flex flex-col items-start gap-1">
                      <span className="block max-w-xs truncate" title={summary}>
                        {summary}
                      </span>
                      {expandableDetail !== null ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggle(request.id)}
                          aria-expanded={isExpanded}
                          // Without aria-controls a screen reader is told this
                          // control expanded something it cannot name; the id
                          // is on the detail row below.
                          aria-controls={creditDetailRowId(request.id)}
                          data-testid={`approval-credit-detail-toggle-${request.id}`}
                        >
                          {isExpanded ? (
                            <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
                          )}
                          {t('approvals.credit.detail_toggle')}
                        </Button>
                      ) : null}
                    </div>
                  </Td>
                  <Td>
                    {lostRace ? (
                      <Chip tone="neutral" testId={`approval-decided-elsewhere-${request.id}`}>
                        {t('approvals.queue.decided_elsewhere')}
                      </Chip>
                    ) : ownRequest ? (
                      <Chip tone="neutral" testId={`approval-own-request-${request.id}`}>
                        {t('approvals.queue.own_request')}
                      </Chip>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => openDecide(request, 'approved')}
                          data-testid={`approval-approve-${request.id}`}
                        >
                          {t('approvals.action.approve')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => openDecide(request, 'rejected')}
                          data-testid={`approval-reject-${request.id}`}
                        >
                          {t('approvals.action.reject')}
                        </Button>
                      </div>
                    )}
                  </Td>
                </Tr>,
                expandableDetail !== null && isExpanded ? (
                  <Tr
                    key={`${request.id}-credit-detail`}
                    id={creditDetailRowId(request.id)}
                    interactive={false}
                  >
                    <Td colSpan={7} className="bg-well/50">
                      {creditDetail !== null ? (
                        <CreditApplicationDetail
                          orgId={creditDetail.org_id}
                          contractAttachmentKey={creditDetail.contract_attachment_key}
                          reason={creditDetail.reason ?? null}
                          registeredAt={request.credit_customer?.registered_at ?? null}
                          paymentSummary={request.credit_customer?.payment_summary ?? null}
                        />
                      ) : temporaryDetail !== null ? (
                        <TemporaryCreditDetail
                          orgId={temporaryDetail.org_id}
                          requestedLimitUsdMicros={null}
                          requestedLimitCnyCents={temporaryDetail.requested_limit_cny_cents}
                          expectedArrivalAmountUsdMicros={null}
                          expectedArrivalAmountCnyCents={
                            temporaryDetail.expected_arrival_amount_cny_cents
                          }
                          expectedArrivalDate={temporaryDetail.expected_arrival_date}
                          paymentEvidenceText={temporaryDetail.payment_evidence_text}
                          paymentEvidenceAttachmentKey={
                            temporaryDetail.payment_evidence_attachment_key
                          }
                          registeredAt={request.credit_customer?.registered_at ?? null}
                          paymentSummary={request.credit_customer?.payment_summary ?? null}
                        />
                      ) : null}
                    </Td>
                  </Tr>
                ) : null,
              ];
            })
          )}
        </TBody>
      </Table>
      {pageCount > 1 ? (
        <Pagination
          page={Math.floor(offset / PAGE_SIZE) + 1}
          pageCount={pageCount}
          onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          prevLabel={t('common.previous')}
          nextLabel={t('common.next')}
          ariaLabel={t('approvals.pagination.aria_label')}
        />
      ) : null}
      <Dialog
        open={target !== null}
        onOpenChange={(open) => {
          if (!open && !decide.isPending) {
            setTarget(null);
          }
        }}
        title={
          target?.decision === 'rejected'
            ? t('approvals.decide.reject_title')
            : t('approvals.decide.approve_title')
        }
        description={
          target !== null ? (
            <span>
              {t(KIND_LABEL_KEYS[target.request.kind])} · <ApprovalTargetRef request={target.request} />
            </span>
          ) : undefined
        }
        closeLabel={t('common.close')}
        testId="approval-decide-dialog"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setTarget(null)}
              disabled={decide.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant={target?.decision === 'rejected' ? 'danger' : 'primary'}
              onClick={submitDecision}
              loading={decide.isPending}
              disabled={rejectNoteMissing}
              data-testid="approval-decide-confirm"
            >
              {target?.decision === 'rejected'
                ? t('approvals.action.reject')
                : t('approvals.action.approve')}
            </Button>
          </>
        }
      >
        <FormField
          label={t('approvals.decide.note_label')}
          fieldId="approval-decide-note"
          required={target?.decision === 'rejected'}
          hint={
            target?.decision === 'rejected' ? t('approvals.decide.note_required_hint') : undefined
          }
        >
          <Textarea
            id="approval-decide-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            data-testid="approval-decide-note"
          />
        </FormField>
      </Dialog>
    </div>
  );
}

function ApprovalTargetRef({ request }: { readonly request: ApprovalRequestRecord }): JSX.Element {
  const orgId = approvalOrgTargetId(request);
  if (orgId === null) {
    return (
      <>
        {request.target_type}:{request.target_id}
      </>
    );
  }
  return (
    <>
      {request.target_type}:<CustomerRef orgId={orgId} />
    </>
  );
}
